/**
 * AddChannelModal Component
 *
 * Modal dialog for creating a new channel in a Microsoft Team.
 * - Channel name (required)
 * - Description (optional)
 * - Channel type: Standard or Private (radio buttons)
 */

import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Label,
  Textarea,
  RadioGroup,
  Radio,
  makeStyles,
  type DialogOpenChangeEvent,
  type DialogOpenChangeData,
} from '@fluentui/react-components';
import { AsyncButton } from '../../common/AsyncButton';
import { Alert } from '../../common/Alert';
import { useCreateChannel } from '../../../hooks/useCreateChannel';

interface AddChannelModalProps {
  tenantId: string;
  teamId: string;
  teamName: string;
  open: boolean;
  onOpenChange: (event: DialogOpenChangeEvent, data: DialogOpenChangeData) => void;
  onSuccess?: () => void;
}

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },

  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },

  label: {
    fontWeight: '500',
    fontSize: '14px',
  },

  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    marginTop: '4px',
  },

  actions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
});

export function AddChannelModal({
  tenantId,
  teamId,
  teamName,
  open,
  onOpenChange,
  onSuccess,
}: AddChannelModalProps) {
  const styles = useStyles();
  const { createChannel, isLoading, error, reset } = useCreateChannel({ tenantId, teamId });

  const [channelName, setChannelName] = useState('');
  const [description, setDescription] = useState('');
  const [membershipType, setMembershipType] = useState<'standard' | 'private'>('standard');

  const resetForm = useCallback(() => {
    setChannelName('');
    setDescription('');
    setMembershipType('standard');
    reset();
  }, [reset]);

  const handleOpenChange = useCallback(
    (event: DialogOpenChangeEvent, data: DialogOpenChangeData) => {
      if (!data.open) {
        resetForm();
      }
      onOpenChange(event, data);
    },
    [onOpenChange, resetForm]
  );

  const handleSubmit = useCallback(async () => {
    if (!channelName.trim()) {
      return;
    }

    const result = await createChannel({
      displayName: channelName.trim(),
      description: description.trim() || undefined,
      membershipType,
    });

    if (result) {
      resetForm();
      onOpenChange({} as DialogOpenChangeEvent, { open: false } as DialogOpenChangeData);
      onSuccess?.();
    }
  }, [channelName, description, membershipType, createChannel, resetForm, onOpenChange, onSuccess]);

  const isSubmitDisabled = !channelName.trim() || isLoading;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Add Channel to {teamName}</DialogTitle>
          <DialogContent className={styles.content}>
            <div className={styles.field}>
              <Label htmlFor="channel-name" className={styles.label}>
                Channel Name
              </Label>
              <Input
                id="channel-name"
                type="text"
                value={channelName}
                onChange={(_e, data) => setChannelName(data.value)}
                placeholder="Enter channel name"
                disabled={isLoading}
              />
            </div>

            <div className={styles.field}>
              <Label htmlFor="channel-description" className={styles.label}>
                Description
              </Label>
              <Textarea
                id="channel-description"
                value={description}
                onChange={(_e, data) => setDescription(data.value)}
                placeholder="Enter channel description (optional)"
                disabled={isLoading}
                resize="vertical"
              />
            </div>

            <div className={styles.field}>
              <Label className={styles.label}>Channel Type</Label>
              <RadioGroup
                value={membershipType}
                onChange={(_e, data) => setMembershipType(data.value as 'standard' | 'private')}
                layout="horizontal"
                className={styles.radioGroup}
                disabled={isLoading}
              >
                <Radio value="standard" label="Standard" />
                <Radio value="private" label="Private" />
              </RadioGroup>
            </div>

            {error && (
              <Alert intent="error">
                Failed to create channel: {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions className={styles.actions}>
            <Button
              appearance="secondary"
              onClick={() => handleOpenChange({} as DialogOpenChangeEvent, { open: false } as DialogOpenChangeData)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <AsyncButton
              appearance="primary"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              loading={isLoading}
              loadingText="Creating..."
            >
              Create
            </AsyncButton>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
